import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isLocale, SUPPORTED_LOCALES, type Locale } from '@emr/shared';

import { applyLocale } from '../../i18n/index.ts';
import {
  readPortalLocalePreference,
  writePortalLocalePreference,
} from './portalLocalePreference.ts';

const BROWSER_DEFAULT = 'browser-default';

const LOCALE_LABEL_KEY: Record<Locale, string> = {
  en: 'languagePicker.locale.en',
  es: 'languagePicker.locale.es',
};

/**
 * The portal's own compact language picker — a native `<select>`, not `LanguageMenu.tsx`'s Radix
 * dropdown, both because this shell has no header room for a second full menu component and because
 * a plain `<select>` is the more mobile-friendly control anyway. Persists to `localStorage`
 * (`portalLocalePreference.ts`'s own header explains why, not the server) rather than
 * `LanguageMenu.tsx`'s account-preference API call.
 */
export function PortalLanguageMenu() {
  const { t } = useTranslation('portalShell');
  const [locale, setLocale] = useState<Locale | null>(() =>
    readPortalLocalePreference(window.localStorage),
  );

  return (
    <label className="flex items-center gap-1.5 text-sm text-ink">
      <span className="sr-only">{t('languagePicker.label')}</span>
      <select
        value={locale ?? BROWSER_DEFAULT}
        onChange={(event) => {
          const { value } = event.target;
          const next = value !== BROWSER_DEFAULT && isLocale(value) ? value : null;
          setLocale(next);
          writePortalLocalePreference(window.localStorage, next);
          applyLocale(next);
        }}
        className="rounded-control border border-line bg-surface px-2 py-2 text-sm font-medium text-ink hover:bg-raised"
      >
        <option value={BROWSER_DEFAULT}>{t('languagePicker.browserDefault')}</option>
        {SUPPORTED_LOCALES.map((code) => (
          <option key={code} value={code}>
            {t(LOCALE_LABEL_KEY[code])}
          </option>
        ))}
      </select>
    </label>
  );
}
